import { z } from 'zod';
/**
 * NFR-42 — "Every list screen must load a block at a time, so that speed does
 * not depend on how many rows exist."
 *
 * ONE paging convention, expressed once, so that forty lists adopt the same
 * shape instead of forty shapes. `features/audit/PAGINATION.md` is the note
 * that argued for this file; `GET /api/audit` is the reference implementation
 * whose four properties are generalised here.
 *
 * ── THE CHOICE: OFFSET (`page`/`pageSize`), NOT A CURSOR ───────────────────
 *
 * PAGINATION.md left this open — "a cursor is the right answer for a feed that
 * only grows (stock movements…)" — so it is decided here, with the reason,
 * because the whole value of a convention is that it is not re-argued per list.
 *
 * Offset, for every list, because:
 *
 *  1. EVERY SCREEN IN THE SPEC STATES A RANGE AND A TOTAL. DSH-09 asks for
 *     "Showing 1–8 of 12"; INV-06's summary cards report "total movements"
 *     against the current filters; every document list in PART 6–9 carries a
 *     count. A cursor cannot produce a total or a page number, so a cursor
 *     list would have to run the same count(*) anyway — at which point it has
 *     paid offset's price and given up offset's affordances.
 *
 *  2. THE DEEP-PAGE ARGUMENT IS ABOUT A COST THIS PRODUCT DOES NOT PAY.
 *     Offset is O(offset) in index entries skipped. Measured on the seeded
 *     tenant of PART 20.7 (60,000 stock movements), the LAST page of the
 *     movement ledger costs single-digit milliseconds because the ORDER BY is
 *     an index scan and the skipped entries are never heap-fetched.
 *     `stock-movements-paging.spec.ts` asserts that deep page directly, so if
 *     a future volume changes the answer, a test says so rather than a user.
 *
 *  3. A CURSOR IS THE WRONG SHAPE FOR A FILTERED LEDGER. Every one of these
 *     lists is filtered by date, kind, warehouse and free text, and a cursor
 *     encodes a position in ONE ordering. Changing a filter invalidates it,
 *     which is the same reset-to-page-one that offset gets for free.
 *
 * ── THE ONE ESCAPE HATCH, NOW TAKEN: ADM-04's NOTIFICATION LIST ───────────
 *
 * This file reserved keyset paging for "an infinite-scroll feed with no total
 * and no filters", and said it was not needed by anything that existed. ADM-04
 * shipped alongside this convention and is that case, so the exception is
 * recorded here — at the convention — rather than left to be rediscovered as
 * an accident in one screen.
 *
 * `GET /api/notifications` takes `before` (the id of the last row shown) and
 * `limit`, and appends. Three reasons, and the first is the one that decides
 * it:
 *
 *  1. THE LIST GROWS AT THE HEAD, and nothing else in this product does. Every
 *     offset list above is a LEDGER filtered by date and read newest-first
 *     from a set that is not moving while somebody reads it. An inbox receives
 *     while it is open — the sweep runs hourly and a cheque can clear
 *     mid-scroll — and an offset would then show a row twice or skip one on
 *     every Load more. On a ledger that is a nuisance; on an inbox it reads as
 *     the list losing messages.
 *
 *  2. THE SPEC ASKS FOR AN APPEND, NOT A JUMP. ADM-04 §18: "A **Load more**
 *     button loading 25 at a time **must** be added." A pager offers a range,
 *     a total and two steps; this control offers one more block. They are
 *     different affordances, and `<Pager>` would be the wrong control drawn
 *     correctly.
 *
 *  3. THERE IS NO TOTAL TO PRINT. Point 1 of the offset argument is that every
 *     screen in the spec states a range and a total — and ADM-04 states
 *     neither. What it states is a count of UNREAD per chip (BR-ADM-04-03),
 *     which is a different number, is not the size of the list, and is
 *     computed from a partial index rather than from a page.
 *
 * WHAT IT DOES NOT DIVERGE ON, deliberately: the chip is a filter, and
 * changing it drops the cursor and starts a fresh list — the same
 * reset-to-the-top that `usePagedState.change` gives every offset screen
 * (BR-DSH-09-19). And the block size is a screen constant (25, fixed by §18),
 * not a URL parameter, for property 4's reason above.
 *
 * A THIRD convention needs the same treatment: an argument in this file, not a
 * choice in a screen.
 *
 * ── THE ESCAPE, AND WHY IT IS SPELLED OUT LOUD ─────────────────────────────
 *
 * `pageSize: 'all'` returns the whole filtered set. It existed because ~15
 * document editors loaded the item catalogue into a `<select>` to resolve a
 * line, and SILENTLY TRUNCATING A PICKER is a worse defect than a slow one: an
 * item past the cut cannot be chosen and nothing on screen says so.
 *
 * It is a STRING, not an omission and not a magic number, for one reason:
 * `grep -rn "'all'"` finds every unbounded caller in the product. The list is
 * pinned by `pagination.spec.ts`, which allows it to SHRINK and not to grow —
 * so the remaining work is a countdown rather than a rediscovery.
 *
 * **That is what it was for, and the reason is now spent.** The picker
 * convention below is the search box those editors were waiting for, and the
 * item catalogue's entry left the list with them. What remains is one caller
 * that is not a picker at all — see `UNPAGED_CALLERS`.
 */
/** The default block. 50 rows fill a screen and cost ~40 KB of JSON. */
export const DEFAULT_PAGE_SIZE = 50;
/**
 * The ceiling on an explicit page size. Not a security boundary — RLS is, and
 * a LIMIT must never become one — but a refusal to stream an unbounded result
 * set into one response by asking for it politely.
 */
export const MAX_PAGE_SIZE = 500;
/** The explicit, greppable opt-out. See the header. */
export const PAGE_SIZE_ALL = 'all';
/**
 * ── THE PICKER CONVENTION (GEN-50), WHICH IS HOW THE ESCAPE ABOVE IS CLOSED ─
 *
 * `GEN-50`: *"Every 'Pick from list' field must offer a search box inside its
 * dropdown once the list holds more than 10 records."* Every unbounded caller
 * in `UNPAGED_CALLERS`, and every `MAX_PAGE_SIZE` picker beside them, exists
 * because that search box did not. A picker without one has to hold the whole
 * list to be correct, and 50,000 items is what "the whole list" means here.
 *
 * A typeahead asks its route TWO questions, and both are bounded:
 *
 *  1. **What matches what the user is typing?** `q` + `pageSize:
 *     PICKER_PAGE_SIZE`. Twenty rows is a panel, not a page: it is what a
 *     280-pixel scroller shows before the reader gives up and types another
 *     letter, and it keeps a keystroke's response in kilobytes.
 *
 *  2. **What is this form ALREADY holding?** `ids` — the ids the document's
 *     saved lines name. This is the half that makes the conversion safe, and
 *     the half a naive typeahead omits: a saved line whose item does not match
 *     the current search must still show its item, or the fix is worse than
 *     the bug it replaced. It is deliberately NOT filtered by `status` either,
 *     because an item deactivated after a document was written still has to
 *     render on that document (BR-MD-01-07 excludes the switched-off from what
 *     a picker OFFERS, never from what it DISPLAYS).
 *
 * `ids` is one comma-joined string rather than a repeated parameter because a
 * query string then survives every client, proxy and log unambiguously, and
 * because the cap belongs in the schema: `MAX_PICKER_IDS` is the ceiling on
 * how many lines one document can ask about at once, and it is bounded for
 * the same reason `MAX_PAGE_SIZE` is.
 */
/** One panel of matches. See the picker convention above. */
export const PICKER_PAGE_SIZE = 20;
/**
 * The ceiling on one `ids` read. 200 lines is the largest document any editor
 * in this product accepts (the line-array caps in the write schemas), so a
 * form can always resolve every value it holds in one bounded request.
 */
export const MAX_PICKER_IDS = 200;
/**
 * The `ids` filter, as a query field: `?ids=<uuid>,<uuid>`.
 *
 * Parsed into an array by the schema so that a service receives ids rather
 * than a string to split — the splitting rule then cannot differ between two
 * routes that both take it.
 */
export const PickerIdsSchema = z
    .string()
    .transform((value) => value.split(',').filter((part) => part.length > 0))
    .pipe(z.uuid().array().min(1).max(MAX_PICKER_IDS));
/** The client half of the same rule, so a caller never hand-joins the string. */
export function pickerIds(ids) {
    return ids.slice(0, MAX_PICKER_IDS).join(',');
}
const PageSizeSchema = z.union([
    z.literal(PAGE_SIZE_ALL),
    z.coerce.number().int().min(1).max(MAX_PAGE_SIZE),
]);
/**
 * The two query fields, as a raw shape so a list contract can spread them into
 * its own `z.object({…}).strict()` beside its filters.
 *
 * Both carry defaults, so every existing caller keeps compiling and every
 * request is bounded whether or not the caller thought about it — which is the
 * half of NFR-42 a convention can actually enforce.
 */
export const PageQueryShape = {
    /** 1-based. A screen resets this to 1 on any filter change. */
    page: z.coerce.number().int().min(1).default(1),
    pageSize: PageSizeSchema.default(DEFAULT_PAGE_SIZE),
};
export const PageQuerySchema = z.object(PageQueryShape).strict();
/**
 * Wave 3B — the optional date window every DOCUMENT list takes beside its
 * paging: bounds on the document's own date (invoice date, order date,
 * delivery date…), both inclusive, either alone. Spread into a list's
 * `pagedQuery` next to its filters; the service applies them to the one
 * date column that names the document.
 */
export const DocumentDateRangeQuery = {
    dateFrom: z.iso.date().optional(),
    dateTo: z.iso.date().optional(),
};
/**
 * The three fields every paged response gains beside its rows.
 *
 * `total` is the count AFTER filtering, always. A total that ignored the
 * filters would make the range printed beside it meaningless — and it is the
 * number the summary cards of INV-05/INV-06 already promise.
 */
export const PageMetaShape = {
    total: z.number().int().min(0),
    page: z.number().int().min(1),
    /** Echoed back so a screen can compute its range without re-deriving it. */
    pageSize: z.number().int().min(1),
};
export const PageMetaSchema = z.object(PageMetaShape).strict();
/**
 * Build a list query schema: the caller's filters plus `page`/`pageSize`.
 *
 * ```ts
 * query: pagedQuery({ q: z.string().optional(), status: StatusSchema.optional() })
 * ```
 */
export function pagedQuery(filters) {
    return z.object({ ...filters, ...PageQueryShape }).strict();
}
/**
 * Wrap a list response. The rows keep the key they already had — `items`,
 * `rows`, `invoices` — so adding paging is ADDITIVE on the wire and no screen
 * has to be rewritten in the same commit as its service.
 *
 * ```ts
 * 200: pagedResponse('items', ItemSchema)
 * ```
 */
export function pagedResponse(key, row) {
    return z
        .object({ [key]: row.array(), ...PageMetaShape })
        .strict();
}
/**
 * Same, for a list that also carries a filter-responsive summary or facets
 * (INV-05's cards, INV-06's cards, DSH-09's dropdowns). The extra keys sit
 * beside the rows rather than inside them, exactly as they do today.
 */
export function pagedResponseWith(key, row, extra) {
    return z.object({ [key]: row.array(), ...extra, ...PageMetaShape }).strict();
}
/**
 * The offset a service should apply, and the limit — `null` for `'all'`.
 *
 * Kept in the contracts package rather than in the API so that the SPA can
 * compute the same range for its "Showing X–Y of Z" line from the same rule.
 */
export function pageBounds(query) {
    if (query.pageSize === PAGE_SIZE_ALL)
        return { limit: null, offset: 0 };
    return { limit: query.pageSize, offset: (query.page - 1) * query.pageSize };
}
/** "Showing 1–8 of 12" — the range DSH-09 §5a field 17 asks every list for. */
export function pageRange(meta) {
    const pages = Math.max(1, Math.ceil(meta.total / meta.pageSize));
    const first = meta.total === 0 ? 0 : (meta.page - 1) * meta.pageSize + 1;
    return { first, last: Math.min(meta.page * meta.pageSize, meta.total), pages };
}
/**
 * THE SHRINKING LIST. Every route a caller still asks for unbounded, and the
 * caller that needs it. `pagination.spec.ts` pins this: an entry may be
 * REMOVED without touching the test, and adding one fails until it is argued
 * for here in writing.
 *
 * Removing an entry means giving its caller a search box instead of a
 * `<select>` — the recipe is in `apps/web/src/features/audit/PAGINATION.md`.
 */
export const UNPAGED_CALLERS = [
    {
        route: 'GET /api/partners/:partnerId/statement',
        caller: 'partner-statement-card.tsx — the payload CRM-04\'s Print button renders',
        why: 'Not a list read. `DocumentActions` prints `renderDocumentHtml(payload)` in the browser and asks the worker for the PDF from the SAME function, and the worker re-reads the whole ledger server-side; a payload built from the page would print page one while the PDF of the same click carried the account, and a statement whose paper and PDF disagree about the closing balance is the one defect this module cannot ship. It is also not a second read in the ordinary case: the query is enabled only when `total > rows.length`, so an account that fits on one page never makes it. The fix is not paging — a printed statement has no pages to turn — it is the render moving server-side entirely, which is what the PDF path already is.',
    },
    {
        route: 'GET /api/reports/statement',
        caller: 'statement-report-page.tsx — the payload CRM-03\'s Print button renders',
        why: 'The entry above, on the Reports-side statement screen (CRM-03): the same `DocumentActions` pair prints `renderDocumentHtml(payload)` locally and asks the worker for the PDF of the same account, so the payload has to be the WHOLE ledger or the paper and the PDF of one click would carry two different closing balances. Conditional exactly as the partner card is — enabled only when `total > rows.length` — so an account that fits on one page never makes the read. It leaves this list when the partner card\'s entry does: when the browser stops rendering documents at all.',
    },
    {
        route: 'GET /api/stock/levels',
        caller: 'transfer-editor-page.tsx — live availability at the source warehouse',
        why: 'Not a list screen. INV-07 §5.5 warns while a clerk types that a line exceeds what is on the shelf, and it builds an item→available map to do it. A paged read gives a map with a hole in it, which warns about nothing and says nothing about why. The fix is a per-item availability lookup, not paging.',
    },
];
//# sourceMappingURL=pagination.js.map